/** Public pages that have reviewed, crawlable content on chainex.io. */
const INDEXABLE_STATIC_PATHS = new Set([
  '/',
  '/about',
  '/api',
  '/bounty',
  '/coins',
  '/earn',
  '/fees',
  '/legal',
  '/maker',
  '/markets',
  '/privacy',
  '/regulatory',
  '/risk',
  '/security',
  '/terms',
  '/travel-rule',
]);
const PRIVATE_ROUTE_PREFIXES = [
  '/account', '/addressbook', '/autologoutdialog', '/balances', '/callback', '/dashboard',
  '/deposit', '/history', '/profile', '/refer', '/settings', '/verificationlevelzeroupdatedialog',
  '/verificationwizard', '/withdraw',
];
const PUBLIC_UTILITY_PREFIXES = [
  '/activate/confirm', '/deactivate/confirm', '/deactivatefallback', '/disabletwofa', '/emailsettings',
  '/forgot', '/login', '/register', '/redirect', '/response', '/signup', '/support', '/unavailable', '/403', '/404',
  '/fergnson',
];

export interface PublicRoutePolicy {
  canonicalPath: string | null;
  renderMode: 'prerender' | 'shell';
  indexable: boolean;
  sitemapEligible: boolean;
}

/** Runtime policy for an already-normalised browser route. Coin details wait for API identity. */
export function getPublicRoutePolicy(path: string): PublicRoutePolicy {
  if (INDEXABLE_STATIC_PATHS.has(path)) {
    return { canonicalPath: path, renderMode: 'prerender', indexable: true, sitemapEligible: true };
  }

  if (/^\/markets\/[A-Z0-9]+\/[A-Z0-9]+$/.test(path)) {
    return { canonicalPath: path, renderMode: 'prerender', indexable: false, sitemapEligible: false };
  }

  if (/^\/markets\/[A-Za-z0-9]+\/[A-Za-z0-9]+$/.test(path)) {
    return { canonicalPath: null, renderMode: 'prerender', indexable: false, sitemapEligible: false };
  }

  if (/^\/coins\/[A-Za-z0-9._-]+$/.test(path)) {
    return { canonicalPath: null, renderMode: 'prerender', indexable: false, sitemapEligible: false };
  }

  if (matchesRoutePrefix(path, PRIVATE_ROUTE_PREFIXES)) {
    return { canonicalPath: null, renderMode: 'shell', indexable: false, sitemapEligible: false };
  }

  if (matchesRoutePrefix(path, PUBLIC_UTILITY_PREFIXES)) {
    return { canonicalPath: null, renderMode: 'prerender', indexable: false, sitemapEligible: false };
  }

  return { canonicalPath: null, renderMode: 'shell', indexable: false, sitemapEligible: false };
}

function matchesRoutePrefix(path: string, prefixes: string[]): boolean {
  return prefixes.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
}

/** Build-time sitemap policy. Coin aliases are rendered but only API-style uppercase codes are listed. */
export function getSitemapCanonicalPath(route: string): string | null {
  const path = route === '' ? '/' : route.replace(/\/+$/, '') || '/';
  if (INDEXABLE_STATIC_PATHS.has(path)) {
    return path;
  }

  if (/^\/coins\/[A-Z0-9]+$/.test(path) && path !== '/coins/CZAR') {
    return path;
  }

  if (/^\/markets\/[A-Z0-9]+\/[A-Z0-9]+$/.test(path)) {
    return path;
  }

  return null;
}

export interface SitemapRouteInventory {
  staticRoutes: string[];
  coinRoutes: string[];
  marketRoutes: string[];
}

/** Fail closed when route discovery or the coin/market APIs return an incomplete set. */
export function buildSitemapRouteInventory(routes: string[]): SitemapRouteInventory {
  const staticRoutes = new Set<string>();
  const coinRoutes = new Set<string>();
  const marketRoutes = new Set<string>();

  routes.forEach(route => {
    const canonicalPath = getSitemapCanonicalPath(route);
    if (!canonicalPath) return;
    if (canonicalPath.startsWith('/markets/')) {
      marketRoutes.add(canonicalPath);
    } else if (canonicalPath.startsWith('/coins/') && canonicalPath !== '/coins') {
      coinRoutes.add(canonicalPath);
    } else {
      staticRoutes.add(canonicalPath);
    }
  });

  const missingRoutes = [...INDEXABLE_STATIC_PATHS].filter(route => !staticRoutes.has(route));
  if (missingRoutes.length || marketRoutes.size === 0 || coinRoutes.size === 0) {
    throw new Error(`Sitemap generation missing required public routes: ${[
      ...missingRoutes,
      ...(marketRoutes.size === 0 ? ['a market pair (market API result set is empty or unavailable)'] : []),
      ...(coinRoutes.size === 0 ? ['a supported coin (coin API result set is empty or unavailable)'] : []),
    ].join(', ')}`);
  }

  return {
    staticRoutes: [...staticRoutes].sort(),
    coinRoutes: [...coinRoutes].sort(),
    marketRoutes: [...marketRoutes].sort(),
  };
}
